<section class="dashboard">
  <div class="dashboard-main">
    <header class="dashboard-header">
      <div>
        <h1>{{ customer()?.name || "Customer" }}</h1>
      </div>
      <div class="header-actions">
        @if (!customer()?.remakeOfCustomerId) {
          <button
            class="action-button"
            (click)="openRemake()"
            [disabled]="!customer() || remakeLoading() || remakeOpen()"
          >
            🔁 Create remake
          </button>
        }
        <button
          class="action-button primary"
          (click)="editCustomer()"
          [disabled]="!customer() || savingCustomer()"
        >
          Edit customer
        </button>
        @if (!customer()?.archivedAt) {
          <button
            class="action-button danger"
            (click)="archiveCustomer()"
            [disabled]="!customer() || archivingCustomer()"
          >
            Archive
          </button>
        }
        <a class="action-button" routerLink="/jobs"> ← Back </a>
      </div>
    </header>

    @if (customer()?.archivedAt; as archivedAt) {
      <div class="archive-banner">
        <span>Archived on {{ archivedAt | date: "MMM dd, yyyy" }}</span>
        <button
          class="action-button primary"
          type="button"
          (click)="restoreCustomer()"
          [disabled]="archivingCustomer()"
        >
          {{ archivingCustomer() ? "Restoring…" : "Restore" }}
        </button>
      </div>
    }

    <div class="stats-grid">
      <div class="stat-badge">
        <div class="badge-value">{{ rooms()?.length || 0 }}</div>
        <div class="badge-label">Rooms</div>
      </div>
      <div class="stat-badge">
        <div class="badge-value">{{ windowsCount() }}</div>
        <div class="badge-label">Windows</div>
      </div>
    </div>

    <nav class="tab-nav" role="tablist" aria-label="Job workspace">
      <button
        type="button"
        role="tab"
        class="tab-button"
        [class.active]="activeTab() === 'overview'"
        [attr.aria-selected]="activeTab() === 'overview'"
        (click)="setTab('overview')"
      >
        Overview
      </button>
      <button
        type="button"
        role="tab"
        class="tab-button"
        [class.active]="activeTab() === 'measurements'"
        [attr.aria-selected]="activeTab() === 'measurements'"
        (click)="setTab('measurements')"
      >
        Measurements
      </button>
      <button
        type="button"
        role="tab"
        class="tab-button"
        [class.active]="activeTab() === 'estimate'"
        [attr.aria-selected]="activeTab() === 'estimate'"
        (click)="setTab('estimate')"
      >
        Estimate
      </button>
    </nav>

    @switch (activeTab()) {
      @case ("overview") {
        @if (customer(); as customer) {
          <div class="panel-card">
            <div class="panel-header">
              <h2>Progress</h2>
            </div>
            <div class="panel-content">
              <ol class="progress-stepper">
                @for (step of jobSteps; track step.status; let i = $index) {
                  <li
                    class="progress-step"
                    [class.is-done]="i < jobStepIndex(customer)"
                    [class.is-current]="i === jobStepIndex(customer)"
                  >
                    <span class="progress-dot" aria-hidden="true"></span>
                    <span class="progress-label">{{ step.label }}</span>
                  </li>
                }
              </ol>
            </div>
          </div>

          <div class="panel-card">
            <div class="panel-header">
              <h2>Status</h2>
            </div>
            <div class="panel-content">
              <div class="form-field-group">
                <label>Customer Status</label>
                <select
                  [(ngModel)]="customer.status"
                  (change)="updateCustomerStatus()"
                  class="form-select"
                  [disabled]="savingCustomer()"
                >
                  <option value="">-- Select Status --</option>
                  <option value="Onboarded">Onboarded</option>
                  <option value="Measured">Measured</option>
                  <option value="Estimate given">Estimate given</option>
                  <option value="Advance paid">Advance paid</option>
                  <option value="In production">In production</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Installed">Installed</option>
                  <option value="Complete paid">Complete paid</option>
                </select>
              </div>
              @if (canEditTracking(customer)) {
                <div class="form-field-group">
                  <label>Tracking Number (SF Express)</label>
                  <input
                    type="text"
                    class="form-input"
                    placeholder="e.g. SF1225351915190"
                    [(ngModel)]="customer.trackingNumber"
                    (change)="saveTracking()"
                    [disabled]="savingCustomer()"
                  />
                  @if (trackUrl(customer); as url) {
                    <a
                      class="track-link"
                      [href]="url"
                      target="_blank"
                      rel="noopener"
                      >Track order</a
                    >
                  }
                </div>
              }
            </div>
          </div>

          <div class="panel-card">
            <div class="panel-header">
              <h2>Send to supplier</h2>
            </div>
            <div class="panel-content">
              <p class="hint-text">
                Routes each blind to its supplier and emails them a portal link.
              </p>

              @if (jobSupplierCards().length) {
                <div class="supplier-cards">
                  @for (s of jobSupplierCards(); track s.id) {
                    <div class="supplier-card" [ngClass]="'stage-' + stageFor(s.status)">
                      <div class="supplier-card-head">
                        <span class="supplier-name">{{ s.name }}</span>
                        <span
                          class="supplier-pill"
                          [ngClass]="'pill-' + stageFor(s.status)"
                        >
                          {{ stageLabel(stageFor(s.status)) }}
                        </span>
                      </div>

                      <ol class="supplier-stepper">
                        @for (
                          step of stepsFor(s.status, !!s.alibabaUrl, s.hasTracking);
                          track step.label
                        ) {
                          <li class="step" [ngClass]="'step-' + step.state">
                            <span class="step-dot" aria-hidden="true"></span>
                            <span class="step-label">{{ step.label }}</span>
                          </li>
                        }
                      </ol>

                      @if (s.scope) {
                        <div class="supplier-scope">{{ s.scope }}</div>
                      }

                      <div class="supplier-foot">
                        <span class="supplier-amount">
                          @if (s.quote; as q) {
                            @if (q.amount != null) {
                              {{ q.amount | currency }}
                            }
                          } @else if (stageFor(s.status) === "awaiting") {
                            <span class="muted">Waiting on supplier</span>
                          }
                        </span>

                        <span class="supplier-actions">
                          @if (stageFor(s.status) === "awaiting" && s.email) {
                            <a class="link-action" [href]="'mailto:' + s.email">
                              Email supplier
                            </a>
                          }
                          @if (stageFor(s.status) === "received") {
                            <button
                              class="btn-mini paid"
                              type="button"
                              (click)="approveJobQuote(s.id)"
                              [disabled]="supplierSaving()"
                            >
                              Approve quote
                            </button>
                            <button
                              class="btn-mini"
                              type="button"
                              (click)="rejectJobQuote(s.id)"
                              [disabled]="supplierSaving()"
                            >
                              Reject
                            </button>
                          }
                          @if (stageFor(s.status) === "approved") {
                            @if (s.alibabaUrl) {
                              <a
                                class="alibaba-pay"
                                [href]="s.alibabaUrl"
                                target="_blank"
                                rel="noopener"
                              >
                                Pay on Alibaba →
                              </a>
                            }
                            <button
                              class="btn-mini paid"
                              type="button"
                              (click)="markJobPaid(s.id)"
                              [disabled]="supplierSaving()"
                            >
                              Mark paid
                            </button>
                          }
                        </span>
                      </div>

                      @if (s.quote || s.hasTracking) {
                        <div class="supplier-docs">
                          @if (s.quote; as q) {
                            <a
                              class="doc-button"
                              [href]="q.url"
                              target="_blank"
                              rel="noopener"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                              Download supplier quote file
                            </a>
                          }
                          @if (s.hasTracking && trackingUrl(); as trackUrl) {
                            <a
                              class="doc-button"
                              [href]="trackUrl"
                              target="_blank"
                              rel="noopener"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-hidden="true"
                              >
                                <rect x="1" y="3" width="15" height="13" />
                                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                                <circle cx="5.5" cy="18.5" r="2.5" />
                                <circle cx="18.5" cy="18.5" r="2.5" />
                              </svg>
                              Track the item
                            </a>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              }

              @if (approveError()) {
                <div class="error-banner" role="alert">{{ approveError() }}</div>
              }
              <button
                class="action-button primary fit"
                type="button"
                (click)="approveOrder()"
                [disabled]="approvingOrder()"
              >
                {{ approvingOrder() ? "Sending…" : "Send to supplier" }}
              </button>
            </div>
          </div>
        }

        @if (customer(); as customer) {
          <div class="panel-card">
            <div class="panel-header">
              <h2>Payments</h2>
            </div>
            <div class="panel-content">
              <div class="payment-summary">
                <span
                  >Total Paid: <strong>{{ totalPaid() | currency }}</strong></span
                >
                @if (customer.orderNumber) {
                  <span class="muted"
                    >Order: <strong>{{ customer.orderNumber }}</strong></span
                  >
                }
                @if (balance(); as b) {
                  <span [class.settled]="b <= 0" [class.owing]="b > 0">
                    Balance:
                    <strong>{{ b <= 0 ? "Settled" : (b | currency) }}</strong>
                  </span>
                } @else {
                  <span class="muted">Balance: — (no estimate)</span>
                }
              </div>

              @if (payments().length > 0) {
                <div class="payment-list">
                  @for (payment of payments(); track payment.id) {
                    <div class="payment-row">
                      <div class="payment-info">
                        <div class="payment-amount">
                          {{ payment.amount | currency }}
                          @if (payment.method) {
                            <span class="payment-method">{{
                              payment.method
                            }}</span>
                          }
                        </div>
                        <div class="payment-meta">
                          {{ payment.date | date: "MMM dd, yyyy" }}
                          @if (payment.note) {
                            <span> · {{ payment.note }}</span>
                          }
                        </div>
                      </div>
                      <button
                        class="payment-remove"
                        type="button"
                        (click)="removePayment(payment.id)"
                        [disabled]="savingPayment()"
                      >
                        Remove
                      </button>
                    </div>
                  }
                </div>
              } @else {
                <p class="hint-text">No payments recorded yet.</p>
              }

              <div class="stripe-pay">
                <div class="stripe-pay-head">Request card payment (Stripe)</div>
                <div class="stripe-pay-row">
                  <input
                    type="number"
                    class="form-input"
                    placeholder="Amount"
                    [(ngModel)]="linkAmount"
                  />
                  <button
                    class="btn-small primary"
                    type="button"
                    (click)="requestStripePayment()"
                    [disabled]="requestingLink()"
                  >
                    {{ requestingLink() ? "Creating…" : "Create Stripe link" }}
                  </button>
                </div>
                @if (paymentLinkError(); as err) {
                  <p class="hint-text error">{{ err }}</p>
                }
                @if (paymentLink(); as url) {
                  <div class="stripe-link">
                    <a [href]="url" target="_blank" rel="noopener">{{ url }}</a>
                    <button class="btn-small" type="button" (click)="copyPaymentLink()">
                      Copy
                    </button>
                  </div>
                  <p class="hint-text">
                    Send this link to the customer — the payment records here once paid.
                  </p>
                }
              </div>

              <div class="payment-form">
                <div class="form-grid">
                  <div class="form-field-group">
                    <label>Amount</label>
                    <input
                      type="number"
                      class="form-input"
                      [(ngModel)]="paymentForm.amount"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                  <div class="form-field-group">
                    <label>Date</label>
                    <input
                      type="date"
                      class="form-input"
                      [(ngModel)]="paymentForm.date"
                    />
                  </div>
                  <div class="form-field-group">
                    <label>Method</label>
                    <input
                      type="text"
                      class="form-input"
                      [(ngModel)]="paymentForm.method"
                      placeholder="check, cash, Zelle…"
                    />
                  </div>
                  <div class="form-field-group">
                    <label>Note</label>
                    <input
                      type="text"
                      class="form-input"
                      [(ngModel)]="paymentForm.note"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <button
                  class="action-button primary"
                  (click)="addPayment()"
                  [disabled]="savingPayment()"
                >
                  {{ savingPayment() ? "Saving…" : "+ Add payment" }}
                </button>
              </div>
            </div>
          </div>
        }

        @if (originalCustomer(); as original) {
          <div class="panel-card">
            <div class="panel-content remake-links">
              <span>
                Remake of
                <a [routerLink]="['/customers', original.id]">{{
                  original.name
                }}</a>
              </span>
            </div>
          </div>
        }

        @if (remakes().length > 0) {
          <div class="panel-card">
            <div class="panel-content remake-links">
              <span>Remakes ({{ remakes().length }}):</span>
              @for (remake of remakes(); track remake.id) {
                <a [routerLink]="['/customers', remake.id]">
                  {{ remake.name }} —
                  {{ remake.createdAt | date: "MMM dd, yyyy" }}
                </a>
              }
            </div>
          </div>
        }
      }

      @case ("measurements") {
        <div class="panel-card">
          <div class="panel-header">
            <h2>Add room</h2>
          </div>
          <div class="panel-content">
            <div class="form-grid">
              <div class="form-field-group">
                <label>Room name</label>
                <select [(ngModel)]="roomForm.name" class="form-select">
                  <option value="" disabled>-- Select room --</option>
                  <option value="living room">living room</option>
                  <option value="Master room">Master room</option>
                  <option value="bedroom 1">bedroom 1</option>
                  <option value="bedroom 2">bedroom 2</option>
                  <option value="bedroom 3">bedroom 3</option>
                  <option value="bedroom 4">bedroom 4</option>
                  <option value="game room">game room</option>
                  <option value="pooja room">pooja room</option>
                  <option value="dining room">dining room</option>
                  <option value="breakfast nook">breakfast nook</option>
                  <option value="media room">media room</option>
                  <option value="office room">office room</option>
                  <option value="patio door">patio door</option>
                  <option value="bathroom 1">bathroom 1</option>
                  <option value="master bathroom">master bathroom</option>
                  <option value="bathroom 2">bathroom 2</option>
                  <option value="bathroom 3">bathroom 3</option>
                  <option value="bathroom 4">bathroom 4</option>
                  <option value="others">others</option>
                </select>
              </div>
              <div class="form-field-group">
                <label>Windows</label>
                <input
                  type="number"
                  class="form-input"
                  min="1"
                  max="20"
                  [(ngModel)]="roomForm.windows"
                />
              </div>
            </div>
            <button
              class="action-button primary"
              (click)="createRoom()"
              [disabled]="creatingRoom()"
            >
              {{ creatingRoom() ? "Adding…" : "+ Add room" }}
            </button>
            <p class="hint-text">
              Adds the room and its windows in one step. Set styles and sizes in
              the grid below.
            </p>
          </div>
        </div>

        @if ((rooms() ?? []).length === 0) {
          <div class="panel-card">
            <div class="panel-content">
              <p class="hint-text">No rooms yet. Add one above.</p>
            </div>
          </div>
        }

        @for (room of rooms(); track room.id) {
          <div class="panel-card room-card">
            <div class="panel-header room-card-header">
              <h2>{{ room.name }}</h2>
              <span class="room-count"
                >{{ (roomWindowCounts() || {})[room.id] || 0 }} windows</span
              >
              <div class="room-card-actions">
                <button
                  class="action-button small"
                  type="button"
                  (click)="addWindow(room)"
                >
                  + Add window
                </button>
                <button
                  class="action-button small danger"
                  type="button"
                  (click)="deleteRoom(room)"
                >
                  Delete
                </button>
              </div>
            </div>
            <div class="panel-content">
              @if (windowsForRoom(room.id).length === 0) {
                <p class="hint-text">No windows in this room.</p>
              } @else {
                <div class="table-wrap">
                  <table class="measure-table">
                    <thead>
                      <tr>
                        <th>Window</th>
                        <th>Style (blind)</th>
                        <th>Control</th>
                        <th>Width</th>
                        <th>Height</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (window of windowsForRoom(room.id); track window.id) {
                        <tr>
                          <td>
                            <input
                              class="form-input label-input"
                              [ngModel]="window.label"
                              (change)="
                                updateWindowLabel(
                                  room,
                                  window,
                                  $any($event.target).value
                                )
                              "
                            />
                          </td>
                          <td>
                            <select
                              class="form-select"
                              [ngModel]="window.style ?? ''"
                              (ngModelChange)="
                                updateWindowStyle(room, window, $event)
                              "
                            >
                              <option value="">—</option>
                              @for (choice of styleChoices(); track choice.value) {
                                <option [value]="choice.value">
                                  {{ choice.label }}
                                </option>
                              }
                            </select>
                          </td>
                          <td>
                            <select
                              class="form-select"
                              [ngModel]="window.controlType ?? 'cordless'"
                              (ngModelChange)="
                                updateWindowControl(room, window, $event)
                              "
                            >
                              <option value="cordless">Cordless</option>
                              <option value="motorized">A-OK Matter motorized</option>
                            </select>
                            @if (window.controlType === "motorized") {
                              <label class="solar-toggle">
                                <input
                                  type="checkbox"
                                  [ngModel]="window.hasSolarPanel ?? false"
                                  (ngModelChange)="
                                    updateWindowSolar(room, window, $event)
                                  "
                                />
                                Solar
                              </label>
                            }
                          </td>
                          <td>
                            <div class="dim-cell">
                              <input
                                type="number"
                                class="form-input dim-input"
                                min="17"
                                max="120"
                                step="0.125"
                                placeholder="—"
                                [ngModel]="measurementFor(window.id)?.width ?? null"
                                (ngModelChange)="setWidth(window, $event)"
                              />
                              <span class="dim-unit">in</span>
                            </div>
                          </td>
                          <td>
                            <div class="dim-cell">
                              <input
                                type="number"
                                class="form-input dim-input"
                                min="12"
                                max="144"
                                step="0.125"
                                placeholder="—"
                                [ngModel]="measurementFor(window.id)?.height ?? null"
                                (ngModelChange)="setHeight(window, $event)"
                              />
                              <span class="dim-unit">in</span>
                            </div>
                          </td>
                          <td class="actions-cell">
                            <a
                              class="action-button small"
                              [routerLink]="[
                                '/customers',
                                customerId(),
                                'rooms',
                                room.id,
                                'windows',
                                window.id,
                                'edit',
                              ]"
                            >
                              Edit
                            </a>
                            <button
                              class="action-button small danger"
                              type="button"
                              (click)="deleteWindow(room, window)"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
            </div>
          </div>
        }
      }

      @case ("estimate") {
        <div class="panel-card">
          <div class="panel-header">
            <h2>Estimate</h2>
          </div>
          <div class="panel-content estimate-summary">
            <div class="estimate-stat">
              <span class="estimate-stat-label">Latest total</span>
              <span class="estimate-stat-value">
                @if (estimateTotal(); as total) {
                  {{ total | currency }}
                } @else {
                  <span class="muted">No estimate yet</span>
                }
              </span>
            </div>
            <div class="estimate-stat">
              <span class="estimate-stat-label">Versions</span>
              <span class="estimate-stat-value">{{ estimateVersionCount() }}</span>
            </div>
            <button
              class="action-button primary"
              type="button"
              (click)="viewEstimate()"
              [disabled]="!customer()"
            >
              Open estimate builder →
            </button>
          </div>
        </div>
      }
    }

    @if (remakeOpen()) {
      <div class="panel-card remake-panel">
        <div class="panel-header">
          <h2>Create remake</h2>
          <button
            class="action-button"
            type="button"
            (click)="closeRemake()"
          >
            Cancel
          </button>
        </div>
        <div class="panel-content">
          <p class="hint-text">
            Tick the windows that were measured wrong and enter the corrected
            sizes. The customer is charged $0; manufacturing cost is recorded on
            the new linked job.
          </p>
          @if (remakeLoading()) {
            <p class="hint-text">Loading windows…</p>
          } @else if (remakeWindows().length === 0 && !remakeMessage()) {
            <p class="hint-text">No measured windows found for this job.</p>
          } @else {
            <div class="remake-window-list">
              @for (row of remakeWindows(); track row.windowId) {
                <label class="remake-window-row">
                  <input
                    type="checkbox"
                    [checked]="row.selected"
                    (change)="toggleRemakeWindow(row.windowId)"
                  />
                  <span class="remake-window-label">
                    {{ row.roomName }} · {{ row.label }}
                  </span>
                  <span class="remake-window-size">
                    <input
                      type="number"
                      class="form-input"
                      [(ngModel)]="row.width"
                      min="0"
                      step="0.01"
                    />
                    <span class="remake-unit">×</span>
                    <input
                      type="number"
                      class="form-input"
                      [(ngModel)]="row.height"
                      min="0"
                      step="0.01"
                    />
                    <span class="remake-unit">{{ row.unit }}</span>
                  </span>
                </label>
              }
            </div>
            <button
              class="action-button primary"
              type="button"
              (click)="submitRemake()"
              [disabled]="remakeSaving()"
            >
              {{ remakeSaving() ? "Creating…" : "Create remake" }}
            </button>
          }
          @if (remakeMessage(); as msg) {
            <p class="status-message">{{ msg }}</p>
          }
        </div>
      </div>
    }

    @if (message(); as message) {
      <div class="status-message">{{ message }}</div>
    }
  </div>
</section>
